export type DataLayerCategory = {
  category_name: Record<string, string>;
};

function getCategoryTranslationMap(
  category: unknown
): Record<string, unknown> | null {
  if (!category || typeof category !== "object") {
    return null;
  }

  const record = category as Record<string, unknown>;
  const translations = record.translations ?? record.locale_translations;

  if (!translations || typeof translations !== "object") {
    return null;
  }

  return translations as Record<string, unknown>;
}

function categoryNameFromTranslation(translation: unknown): string {
  if (typeof translation === "string") {
    return translation;
  }

  if (translation && typeof translation === "object" && "name" in translation) {
    const name = (translation as { name?: unknown }).name;
    return typeof name === "string" ? name : "";
  }

  return "";
}

export function formatDataLayerCategories(
  productCategories: unknown[] | null | undefined
): DataLayerCategory[] {
  if (!Array.isArray(productCategories)) {
    return [];
  }

  return productCategories.flatMap((category) => {
    const translationMap = getCategoryTranslationMap(category);
    if (!translationMap) {
      return [];
    }

    const category_name = Object.fromEntries(
      Object.entries(translationMap).map(([locale, translation]) => [
        locale,
        categoryNameFromTranslation(translation),
      ])
    );

    return [{ category_name }];
  });
}

export function formatDataLayerProductName(
  translations: unknown
): Record<string, { name: string }> {
  if (!translations || typeof translations !== "object") {
    return {};
  }

  return Object.fromEntries(
    Object.entries(translations as Record<string, unknown>).map(
      ([locale, value]) => {
        let displayName = "";

        if (value && typeof value === "object") {
          const record = value as Record<string, unknown>;
          if (typeof record.display_name === "string") {
            displayName = record.display_name;
          } else if (typeof record.displayName === "string") {
            displayName = record.displayName;
          } else if (typeof record.name === "string") {
            displayName = record.name;
          }
        }

        return [locale, { name: displayName }];
      }
    )
  );
}
